import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import AsoEbiGuestView from './AsoEbiGuestView'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import FallingPetals from '../../components/FallingPetals'
import CashGiftModal from '../../components/CashGiftModal'
import { useSwipeTabs } from '../../lib/swipe'
import { compressImage } from '../../lib/imageCompression'
import { Heart, Calendar, CalendarPlus, MapPin, Gift, Camera, Shirt, Star, Wallet } from 'lucide-react'

// ── Save an event to the guest's calendar (downloads a universal .ics file) ──
const _p2 = n => String(n).padStart(2, '0')
const _icsDate  = d => `${d.getUTCFullYear()}${_p2(d.getUTCMonth() + 1)}${_p2(d.getUTCDate())}`
const _icsStamp = d => `${d.getUTCFullYear()}${_p2(d.getUTCMonth() + 1)}${_p2(d.getUTCDate())}T${_p2(d.getUTCHours())}${_p2(d.getUTCMinutes())}${_p2(d.getUTCSeconds())}Z`
const _icsEsc   = s => String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
function saveEventToCalendar(ev, couple) {
  if (!ev?.date) return
  const start = new Date(ev.date)
  const end   = new Date(start.getTime() + 86400000)   // all-day → ends next day
  const desc = [`You're invited to ${ev.type} for ${couple}'s wedding.`]
  if (ev.dress_code) desc.push(`Dress code: ${ev.dress_code}${ev.dress_code_fabric ? ` (${ev.dress_code_fabric})` : ''}`)
  if (ev.strictly_invited && !ev.venue) desc.push('Venue is shared on your personal invitation.')
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Okoiya//Wedding//EN', 'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${ev.id || Math.random().toString(36).slice(2)}@okoiya.com`,
    `DTSTAMP:${_icsStamp(new Date())}`,
    `DTSTART;VALUE=DATE:${_icsDate(start)}`,
    `DTEND;VALUE=DATE:${_icsDate(end)}`,
    `SUMMARY:${_icsEsc(`${ev.type} — ${couple}`)}`,
    ev.venue ? `LOCATION:${_icsEsc(ev.venue)}` : null,
    `DESCRIPTION:${_icsEsc(desc.join('\n'))}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean)
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${(ev.type || 'event').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ── "Add to Calendar" pill with a small menu (Google / Outlook / .ics) ──
function AddToCalendar({ ev, couple }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [open])
  if (!ev?.date) return null
  const start = new Date(ev.date)
  const end   = new Date(start.getTime() + 86400000)   // all-day → ends next day
  const title = `${ev.type} — ${couple}`
  const desc  = `You're invited to ${ev.type} for ${couple}'s wedding.`
  const isoUTC = d => `${d.getUTCFullYear()}-${_p2(d.getUTCMonth() + 1)}-${_p2(d.getUTCDate())}`
  const loc = ev.venue ? `&location=${encodeURIComponent(ev.venue)}` : ''
  const google  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${_icsDate(start)}/${_icsDate(end)}&details=${encodeURIComponent(desc)}${loc}`
  const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&allday=true&subject=${encodeURIComponent(title)}&startdt=${isoUTC(start)}&enddt=${isoUTC(end)}&body=${encodeURIComponent(desc)}${loc}`
  const item = { display:'block', width:'100%', textAlign:'left', boxSizing:'border-box', background:'none', border:'none', padding:'0.6rem 0.95rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#1A1A1A', textDecoration:'none', whiteSpace:'nowrap' }
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button onClick={()=>setOpen(o=>!o)} style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'rgba(196,130,106,0.08)', border:'1px solid rgba(196,130,106,0.25)', borderRadius:100, padding:'0.45rem 1rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#C4826A' }}>
        <CalendarPlus size={13}/> Add to Calendar
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, background:'#fff', border:'1px solid #E5E0D8', borderRadius:'0.75rem', boxShadow:'0 12px 32px rgba(0,0,0,0.14)', overflow:'hidden', zIndex:30, minWidth:170 }}>
          <a href={google} target="_blank" rel="noopener noreferrer" onClick={()=>setOpen(false)} style={item}>Google Calendar</a>
          <a href={outlook} target="_blank" rel="noopener noreferrer" onClick={()=>setOpen(false)} style={{ ...item, borderTop:'1px solid #F3F0EA' }}>Outlook</a>
          <button onClick={()=>{ saveEventToCalendar(ev, couple); setOpen(false) }} style={{ ...item, borderTop:'1px solid #F3F0EA' }}>Apple / Other (.ics)</button>
        </div>
      )}
    </div>
  )
}


// ── Floating Petals / Particles ──────────────────────────────
const PETAL_CONFIG = [
  // Negative delays = already mid-fall at page load, positive = starts fresh
  { left:'4%',  delay:-8,   dur:17, size:5,  rot:25,  sway:22, opacity:0.18 },
  { left:'9%',  delay:-14,  dur:20, size:8,  rot:-18, sway:28, opacity:0.22 },
  { left:'14%', delay:-4,   dur:16, size:4,  rot:35,  sway:18, opacity:0.15 },
  { left:'19%', delay:-11,  dur:19, size:6,  rot:-30, sway:24, opacity:0.20 },
  { left:'24%', delay:-7,   dur:18, size:3,  rot:20,  sway:32, opacity:0.17 },
  { left:'29%', delay:-16,  dur:20, size:7,  rot:-15, sway:20, opacity:0.23 },
  { left:'35%', delay:-2,   dur:17, size:4,  rot:40,  sway:26, opacity:0.16 },
  { left:'40%', delay:-13,  dur:19, size:6,  rot:-25, sway:30, opacity:0.21 },
  { left:'45%', delay:-9,   dur:16, size:5,  rot:18,  sway:22, opacity:0.19 },
  { left:'50%', delay:-5,   dur:20, size:3,  rot:-40, sway:28, opacity:0.15 },
  { left:'55%', delay:-17,  dur:18, size:8,  rot:30,  sway:16, opacity:0.22 },
  { left:'60%', delay:-12,  dur:17, size:4,  rot:-20, sway:34, opacity:0.17 },
  { left:'66%', delay:-3,   dur:19, size:6,  rot:22,  sway:24, opacity:0.20 },
  { left:'71%', delay:-15,  dur:16, size:3,  rot:-35, sway:30, opacity:0.15 },
  { left:'76%', delay:-6,   dur:20, size:7,  rot:28,  sway:20, opacity:0.23 },
  { left:'81%', delay:-10,  dur:17, size:5,  rot:-18, sway:26, opacity:0.18 },
  { left:'86%', delay:-18,  dur:19, size:4,  rot:45,  sway:22, opacity:0.16 },
  { left:'91%', delay:-1,   dur:18, size:6,  rot:-30, sway:28, opacity:0.21 },
  { left:'96%', delay:-7,   dur:20, size:3,  rot:15,  sway:32, opacity:0.15 },
  { left:'11%', delay:-19,  dur:17, size:5,  rot:-22, sway:20, opacity:0.19 },
  { left:'37%', delay:-4,   dur:19, size:7,  rot:32,  sway:26, opacity:0.22 },
  { left:'62%', delay:-13,  dur:16, size:4,  rot:-28, sway:30, opacity:0.17 },
  { left:'84%', delay:-8,   dur:18, size:6,  rot:20,  sway:18, opacity:0.20 },
]

function FloatingPetals() {
  return (
    <div style={{ position:'absolute', inset:0, bottom:'-2px', overflow:'hidden', pointerEvents:'none', zIndex:1 }}>
      <style>{`
        @keyframes petal-fall {
          0%   { transform: translateY(0px) translateX(0) rotate(0deg) scale(1); opacity: var(--op); }
          5%   { opacity: var(--op); }
          50%  { transform: translateY(260px) translateX(calc(var(--sway) * 0.5)) rotate(calc(var(--spin) * 0.5)) scale(1); }
          94%  { opacity: var(--op); }
          100% { transform: translateY(520px) translateX(var(--sway)) rotate(var(--spin)); opacity: 0; }
        }
        .petal {
          position: absolute;
          top: 0;
          animation: petal-fall var(--dur) cubic-bezier(0.25,0.1,0.25,1) var(--delay) infinite;
          will-change: transform, opacity;
        }
      `}</style>
      {PETAL_CONFIG.map((p, i) => (
        <div key={i} className="petal" style={{
          left: p.left,
          '--dur': `${p.dur}s`,
          '--delay': `${p.delay}s`,
          '--sway': `${(i % 2 === 0 ? 1 : -1) * p.sway}px`,
          '--rot': `${(i % 2 === 0 ? 1 : -1) * 180}deg`,
          opacity: p.opacity,
        }}>
          {/* Petal SVG — soft organic teardrop shape */}
          <svg width={p.size * 2.2} height={p.size * 3} viewBox="0 0 11 16" fill="none">
            <path
              d="M5.5 0C5.5 0 0 5 0 9.5C0 12.5 2.5 16 5.5 16C8.5 16 11 12.5 11 9.5C11 5 5.5 0 5.5 0Z"
              fill="rgba(240,200,180,0.9)"
            />
            <path
              d="M5.5 2C5.5 2 1.5 6.5 1.5 9.5C1.5 12 3.2 14.5 5.5 14.5"
              stroke="rgba(255,235,220,0.4)" strokeWidth="0.8" fill="none"
            />
          </svg>
        </div>
      ))}
    </div>
  )
}


// ── Image Lightbox ───────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex)
  const [touchStart, setTouchStart] = useState(null)
  const img = images[idx]

  function prev() { setIdx(i => i > 0 ? i - 1 : images.length - 1) }
  function next() { setIdx(i => i < images.length - 1 ? i + 1 : 0) }

  function handleKey(e) {
    if (e.key === 'ArrowLeft') prev()
    else if (e.key === 'ArrowRight') next()
    else if (e.key === 'Escape') onClose()
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  })

  async function download() {
    try {
      const res = await fetch(img)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `okoiya-photo-${idx+1}.jpg`
      a.click(); URL.revokeObjectURL(url)
    } catch(e) { window.open(img, '_blank') }
  }

  function share() {
    if (navigator.share) {
      navigator.share({ title: 'Wedding Photo', url: img }).catch(()=>{})
    } else {
      navigator.clipboard.writeText(img)
      alert('Photo link copied!')
    }
  }

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:200, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      {/* Close */}
      <button onClick={onClose} style={{ position:'absolute', top:16, right:16, background:'none', border:'none', color:'white', fontSize:'1.5rem', cursor:'pointer', zIndex:201 }}>✕</button>
      {/* Counter */}
      <p style={{ position:'absolute', top:20, left:'50%', transform:'translateX(-50%)', color:'rgba(255,255,255,0.6)', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', margin:0 }}>{idx+1} / {images.length}</p>
      {/* Image */}
      <img src={img} alt="" onClick={e=>e.stopPropagation()}
        onTouchStart={e=>setTouchStart(e.touches[0].clientX)}
        onTouchEnd={e=>{ if(!touchStart)return; const diff=e.changedTouches[0].clientX-touchStart; if(diff>50)prev(); if(diff<-50)next(); setTouchStart(null) }}
        style={{ maxWidth:'90vw', maxHeight:'80vh', objectFit:'contain', borderRadius:'0.5rem', userSelect:'none' }}/>
      {/* Nav arrows */}
      {images.length > 1 && <>
        <button onClick={e=>{e.stopPropagation();prev()}} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.15)', border:'none', color:'white', fontSize:'1.5rem', width:44, height:44, borderRadius:'50%', cursor:'pointer', backdropFilter:'blur(4px)' }}>‹</button>
        <button onClick={e=>{e.stopPropagation();next()}} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.15)', border:'none', color:'white', fontSize:'1.5rem', width:44, height:44, borderRadius:'50%', cursor:'pointer', backdropFilter:'blur(4px)' }}>›</button>
      </>}
      {/* Actions */}
      <div onClick={e=>e.stopPropagation()} style={{ position:'absolute', bottom:24, display:'flex', gap:'0.75rem' }}>
        <button onClick={download} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:600, backdropFilter:'blur(4px)' }}>⬇ Download</button>
        <button onClick={share} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:600, backdropFilter:'blur(4px)' }}>↗ Share</button>
      </div>
    </div>
  )
}

// ── Countdown ─────────────────────────────────────────────
function CountdownHero({ date }) {
  const [time, setTime] = useState({})
  useEffect(() => {
    function calc() {
      const diff = new Date(date) - new Date()
      if (diff <= 0) { setTime({ done:true }); return }
      setTime({
        d: Math.floor(diff/86400000),
        h: Math.floor((diff%86400000)/3600000),
        m: Math.floor((diff%3600000)/60000),
        s: Math.floor((diff%60000)/1000),
      })
    }
    calc(); const t = setInterval(calc,1000); return ()=>clearInterval(t)
  },[date])
  if (!date) return null
  if (time.done) return <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'rgba(255,255,255,0.5)',letterSpacing:'0.1em' }}>TODAY IS THE DAY ✦</p>
  return (
    <div style={{ display:'flex',gap:'0.75rem',justifyContent:'center',flexWrap:'wrap' }}>
      {[{v:time.d,l:'Days'},{v:time.h,l:'Hours'},{v:time.m,l:'Mins'},{v:time.s,l:'Secs'}].map(({v,l})=>(
        <div key={l} style={{ textAlign:'center',minWidth:48 }}>
          <p style={{ fontFamily:'Gibs, serif',fontSize:'clamp(1.4rem,4vw,2rem)',color:'white',margin:0,fontWeight:400,lineHeight:1 }}>{String(v||0).padStart(2,'0')}</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.58rem',color:'rgba(255,255,255,0.4)',margin:'3px 0 0',letterSpacing:'0.1em',textTransform:'uppercase' }}>{l}</p>
        </div>
      ))}
    </div>
  )
}

// ── Countdown hero (light theme) ─────────────────────────
function CountdownHeroLight({ date, onDark }) {
  const [time, setTime] = useState({})
  useEffect(() => {
    function calc() {
      const diff = new Date(date) - new Date()
      if (diff <= 0) { setTime({ done:true }); return }
      setTime({ d:Math.floor(diff/86400000), h:Math.floor((diff%86400000)/3600000), m:Math.floor((diff%3600000)/60000), s:Math.floor((diff%60000)/1000) })
    }
    calc(); const t = setInterval(calc,1000); return ()=>clearInterval(t)
  },[date])
  const numColor   = onDark ? '#FFFFFF' : '#1A1A1A'
  const labelColor = onDark ? 'rgba(255,255,255,0.7)' : 'rgba(28,20,16,0.4)'
  if (!date) return null
  if (time.done) return <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:onDark?'#FFFFFF':'#9BAF9B',letterSpacing:'0.12em',fontWeight:700}}>TODAY IS THE DAY ✦</p>
  return (
    <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
      {[{v:time.d,l:'Days'},{v:time.h,l:'Hours'},{v:time.m,l:'Mins'},{v:time.s,l:'Secs'}].map(({v,l})=>(
        <div key={l} style={{textAlign:'center',minWidth:52}}>
          <p style={{fontFamily:'Gibs, serif',fontSize:'clamp(1.8rem,5vw,2.8rem)',color:numColor,margin:0,fontWeight:400,lineHeight:1,textShadow:onDark?'0 1px 12px rgba(0,0,0,0.35)':'none'}}>{String(v||0).padStart(2,'0')}</p>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.58rem',color:labelColor,margin:'4px 0 0',letterSpacing:'0.12em',textTransform:'uppercase'}}>{l}</p>
        </div>
      ))}
    </div>
  )
}

// Guest portal hero presets. Couples pick one in Settings; custom photo upload
// (premium) is stored in hero_image_url with hero_preset='photo'.
const HERO_PRESETS = {
  floral:  { label:'Floral',        type:'image',    src:'/hero-floral.webp', bg:'linear-gradient(135deg,#F7E4DB,#ECCDBE,#F2DACE)' },
  blush:   { label:'Blush',         type:'gradient', bg:'linear-gradient(135deg,#F6E2DA,#EBC7B8,#F3D9CC)' },
  sage:    { label:'Sage',          type:'gradient', bg:'linear-gradient(135deg,#E4EBE0,#C7D6C0,#EAF0E6)' },
  classic: { label:'Classic cream', type:'gradient', bg:'linear-gradient(-45deg,#F5EDE0,#FAFAF8,#F0E6DA,#FAFAF8,#EDE0D4)' },
}

// ── Registry Section ──────────────────────────────────────
function RegistrySection({ weddingId, wedding, slug, refreshKey }) {
  const [items,        setItems]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [reserving,    setReserving]    = useState(null) // item being reserved
  const [guestName,    setGuestName]    = useState('')
  const [guestMsg,     setGuestMsg]     = useState('')
  const [reserveQty,   setReserveQty]   = useState(1)
  const [confirmed,    setConfirmed]    = useState({})
  const [priceFilter,  setPriceFilter]  = useState('all')
  const [giftFor,      setGiftFor]      = useState(null) // null | {} general | {itemId, fundName}

  useEffect(()=>{
    supabase.rpc('get_public_registry', { p_wedding_id: weddingId })
      .then(({data})=>{
        const enriched = (data||[]).map(item => {
          const reserved = item.quantity_reserved || 0
          return { ...item, quantity_reserved: reserved, status: reserved >= (item.quantity_needed||1) ? 'reserved' : 'available' }
        })
        setItems(enriched); setLoading(false)
      })
  },[weddingId, refreshKey])

  async function notifyCouple(title, message, eventType = 'registry') {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      await fetch(`${supabaseUrl}/functions/v1/notify-couple`, {
        method:'POST',
        headers:{'Content-Type':'application/json','apikey':import.meta.env.VITE_SUPABASE_ANON_KEY,'Authorization':`Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`},
        body:JSON.stringify({ wedding_id: weddingId, title, message, event_type: eventType })
      })
    } catch(e) {}
  }

  async function reserve(item) {
    if (!guestName.trim()) return
    const qty = reserveQty || 1
    setReserving(item.id)
    // Insert one reservation row with quantity
    await supabase.from('registry_reservations').insert({
      item_id: item.id, wedding_id: weddingId,
      guest_name: guestName.trim(), guest_message: guestMsg,
      quantity: qty
    })
    const newReserved = (item.quantity_reserved||0) + qty
    const newStatus = newReserved >= (item.quantity_needed||1) ? 'reserved' : 'available'
    // Update local state only — reservation row is the source of truth, no UPDATE needed on registry_items
    setConfirmed(prev=>({...prev,[item.id]:true}))
    setItems(prev=>prev.map(i=>i.id===item.id?{
      ...i,
      quantity_reserved: newReserved,
      status: newStatus,
      registry_reservations: [...(i.registry_reservations||[]), { id: Date.now(), guest_name: guestName.trim(), quantity: qty }]
    }:i))
    setReserving(null); setGuestName(''); setGuestMsg(''); setReserveQty(1)
    notifyCouple('🎁 Gift Reserved!', `${guestName.trim()} just reserved ${qty > 1 ? qty + ' × ' : ''}${item.name}`)
  }

  const filtered = items.filter(i=>{
    if (priceFilter==='under50k')  return Number(i.price)<50000
    if (priceFilter==='50k-200k')  return Number(i.price)>=50000&&Number(i.price)<=200000
    if (priceFilter==='over200k')  return Number(i.price)>200000
    if (priceFilter==='cash')      return i.is_cash_fund
    return true
  })

  if (loading) return <p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem'}}>Loading registry…</p>

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
      {giftFor && <CashGiftModal weddingId={weddingId} itemId={giftFor.itemId||null} fundName={giftFor.fundName} currency={wedding?.currency || 'NGN'} onClose={()=>setGiftFor(null)}/>}

      {wedding?.gifts_enabled && (
        <button onClick={()=>setGiftFor({})} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.5rem',width:'100%',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.9rem',color:'white',background:'linear-gradient(135deg,#C4826A,#B36B55)',border:'none',borderRadius:100,padding:'0.8rem 1.5rem',cursor:'pointer',boxShadow:'0 4px 14px rgba(196,130,106,0.3)'}}>
          🎁 Send a cash gift
        </button>
      )}

      {/* Guest's running receipt of gifts sent to this wedding */}
      {(() => {
        let receipt = []
        try { receipt = JSON.parse(localStorage.getItem(`okoiya_gifts_${slug}`) || '[]') } catch (_) {}
        if (!receipt.length) return null
        const total = receipt.reduce((s, g) => s + Number(g.amount || 0), 0)
        return (
          <div style={{ background:'linear-gradient(135deg,#FBF4EE,#F6EFE8)', border:'1px solid rgba(196,130,106,0.25)', borderRadius:'1rem', padding:'1rem 1.125rem' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'#C4826A', margin:'0 0 0.5rem' }}>Your gifts 💛</p>
            {receipt.map((g, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.25rem 0', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B' }}>
                <span>{new Date(g.at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</span>
                <span style={{ fontWeight:700, color:'#1A1A1A' }}>₦{Number(g.amount).toLocaleString()}</span>
              </div>
            ))}
            <div style={{ borderTop:'1px solid rgba(196,130,106,0.2)', margin:'0.4rem 0', paddingTop:'0.4rem', display:'flex', justifyContent:'space-between', alignItems:'center', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:800, color:'#1A1A1A' }}>
              <span>Total sent</span><span>₦{total.toLocaleString()}</span>
            </div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:'0.4rem 0 0' }}>
              Thank you for blessing {wedding?.partner1_name?.split(' ')[0]} &amp; {wedding?.partner2_name?.split(' ')[0]} 💛
            </p>
          </div>
        )
      })()}

      {/* Price filter */}
      <div style={{display:'flex',gap:'0.4rem',overflowX:'auto',scrollbarWidth:'none'}}>
        {[{v:'all',l:'All'},{v:'under50k',l:'Under ₦50k'},{v:'50k-200k',l:'₦50k–₦200k'},{v:'over200k',l:'₦200k+'},{v:'cash',l:'Cash Funds'}].map(t=>(
          <button key={t.v} onClick={()=>setPriceFilter(t.v)} style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:priceFilter===t.v?700:500,padding:'0.3rem 0.75rem',borderRadius:100,border:`1px solid ${priceFilter===t.v?'#C4826A':'#E5E0D8'}`,background:priceFilter===t.v?'rgba(196,130,106,0.08)':'white',color:priceFilter===t.v?'#C4826A':'#6B6B6B',cursor:'pointer',flexShrink:0}}>
            {t.l}
          </button>
        ))}
      </div>

      {filtered.length===0&&<p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem',fontSize:'0.875rem'}}>No items in this category.</p>}

      <div style={{display:'flex',flexDirection:'column',gap:'0.875rem'}}>
        {[...filtered].sort((a,b)=>{
          const isResA = (!a.is_cash_fund&&(a.quantity_needed||1)-(a.quantity_reserved||0)<=0)?1:0
          const isResB = (!b.is_cash_fund&&(b.quantity_needed||1)-(b.quantity_reserved||0)<=0)?1:0
          if (isResA !== isResB) return isResA - isResB
          return new Date(b.created_at||0) - new Date(a.created_at||0)
        }).map(item=>{
          const qty=item.quantity_needed||1
          const res=item.quantity_reserved||0
          const available=qty-res
          const isReserved=!item.is_cash_fund&&(available<=0||confirmed[item.id])
          const cashPct=item.is_cash_fund&&item.cash_target?Math.min(((item.cash_raised||0)/item.cash_target)*100,100):0
          return (
            <div key={item.id} style={{background:'white',border:`1px solid ${isReserved?'rgba(155,175,155,0.4)':'#E5E0D8'}`,borderRadius:'1rem',padding:'1rem',display:'flex',gap:'0.875rem',alignItems:'flex-start',opacity:isReserved?0.65:1,boxShadow:'0 1px 2px rgba(0,0,0,0.03)',transition:'border-color 0.18s, box-shadow 0.18s'}}
              onMouseEnter={e=>{ if(!isReserved){ e.currentTarget.style.boxShadow='0 8px 24px rgba(196,130,106,0.10)'; e.currentTarget.style.borderColor='rgba(196,130,106,0.35)' } }}
              onMouseLeave={e=>{ e.currentTarget.style.boxShadow='0 1px 2px rgba(0,0,0,0.03)'; e.currentTarget.style.borderColor=isReserved?'rgba(155,175,155,0.4)':'#E5E0D8' }}>
              {/* Image */}
              <div style={{width:58,height:58,borderRadius:'0.875rem',background:item.image_url?'#F3F0EA':'rgba(196,130,106,0.08)',flexShrink:0,overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center'}}>
                {item.image_url
                  ? <img src={item.image_url} alt={item.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                  : (item.is_cash_fund ? <Wallet size={23} color="#C4826A"/> : <Gift size={23} color="#C4826A"/>)}
              </div>
              {/* Details */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'0.25rem',flexWrap:'wrap'}}>
                  <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',color:'#1A1A1A',margin:0}}>{item.name}</p>
                  {item.priority==='most_needed'&&<span style={{display:'inline-flex',alignItems:'center',gap:'3px',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.6rem',fontWeight:700,color:'#C4826A',background:'rgba(196,130,106,0.1)',borderRadius:100,padding:'2px 7px',flexShrink:0}}><Star size={9} fill="#C4826A" color="#C4826A"/> Most Needed</span>}
                </div>
                {item.description&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#6B6B6B',margin:'0.15rem 0 0',lineHeight:1.5}}>{item.description}</p>}
                {!item.is_cash_fund&&item.price&&<p style={{fontFamily:'Gibs, serif',fontSize:'0.95rem',color:'#C4826A',margin:'0.25rem 0 0'}}>₦{Number(item.price).toLocaleString()}</p>}
                {!item.is_cash_fund&&qty>1&&!isReserved&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#9BAF9B',margin:'0.2rem 0 0',fontWeight:600}}>{available} of {qty} still available</p>}

                {/* Cash fund progress */}
                {item.is_cash_fund&&item.cash_target&&(
                  <div style={{marginTop:'0.5rem'}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'0.25rem'}}>
                      <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#6B6B6B'}}>₦{(item.cash_raised||0).toLocaleString()} raised</span>
                      <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#6B6B6B'}}>of ₦{Number(item.cash_target).toLocaleString()}</span>
                    </div>
                    <div style={{height:6,background:'#F3F0EA',borderRadius:100}}>
                      <div style={{height:'100%',width:`${cashPct}%`,background:'#C4826A',borderRadius:100,transition:'width 0.4s'}}/>
                    </div>
                    <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',color:'#BCBCBC',margin:'0.2rem 0 0'}}>{Math.round(cashPct)}% funded</p>
                  </div>
                )}

                {/* Action area */}
                <div style={{marginTop:'0.75rem'}}>
                  {/* Physical — reserve */}
                  {!item.is_cash_fund&&!isReserved&&reserving!==item.id&&(
                    <button onClick={()=>setReserving(item.id)} style={{background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.45rem 1rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.78rem'}}>
                      Reserve This Gift
                    </button>
                  )}
                  {/* Reserve form */}
                  {!item.is_cash_fund&&reserving===item.id&&(
                    <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                      <input placeholder="Your name *" value={guestName} onChange={e=>setGuestName(e.target.value)}
                        style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',padding:'0.5rem 0.75rem',border:'1.5px solid #E5E0D8',borderRadius:'0.75rem',outline:'none'}}/>
                      {/* Quantity selector — only for multi-quantity items */}
                      {available>1&&(
                        <div>
                          <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B',display:'block',marginBottom:'0.25rem'}}>How many? ({reserveQty} of {available})</label>
                          <input type="range" min={1} max={available} value={reserveQty} onChange={e=>setReserveQty(Number(e.target.value))}
                            style={{width:'100%',accentColor:'#C4826A'}}/>
                        </div>
                      )}
                      <input placeholder="Leave a message (optional)" value={guestMsg} onChange={e=>setGuestMsg(e.target.value)}
                        style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',padding:'0.5rem 0.75rem',border:'1.5px solid #E5E0D8',borderRadius:'0.75rem',outline:'none'}}/>
                      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#BCBCBC',margin:0,lineHeight:1.5}}>Reserving means you plan to bring or send this gift.</p>
                      <div style={{display:'flex',gap:'0.4rem'}}>
                        <button onClick={()=>{setReserving(null);setReserveQty(1)}} style={{flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',padding:'0.4rem',borderRadius:100,border:'1px solid #E5E0D8',background:'white',cursor:'pointer',color:'#6B6B6B'}}>Cancel</button>
                        <button onClick={()=>reserve(item)} disabled={!guestName.trim()} style={{flex:2,background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.4rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.75rem',opacity:!guestName.trim()?0.5:1}}>
                          Reserve {reserveQty>1?`${reserveQty} items`:''}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Reserved state */}
                  {!item.is_cash_fund&&isReserved&&(
                    <div style={{background:'rgba(155,175,155,0.1)',borderRadius:'0.625rem',padding:'0.4rem 0.75rem',display:'inline-block'}}>
                      <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#7A917A',fontWeight:600}}>{confirmed[item.id]?'Reserved! Thank you 🎉':'Already reserved ✓'}</span>
                    </div>
                  )}

                  {/* Cash fund — contribute via real Paystack gifts only.
                       The old honor-system flow (public account number + a
                       client-side cash_raised write) has been retired. */}
                  {item.is_cash_fund&&(
                    wedding?.gifts_enabled ? (
                      <button onClick={()=>setGiftFor({itemId:item.id, fundName:item.name})} style={{background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.45rem 1rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.78rem'}}>
                        Contribute
                      </button>
                    ) : (
                      <div style={{display:'flex',alignItems:'center',gap:'0.45rem',background:'rgba(196,130,106,0.06)',border:'1px solid rgba(196,130,106,0.18)',borderRadius:'0.625rem',padding:'0.5rem 0.75rem'}}>
                        <Wallet size={13} color="#C4826A" style={{flexShrink:0}}/>
                        <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B',fontWeight:600,lineHeight:1.45}}>The couple is finalizing their cash-gift setup — check back soon 💛</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Download Full Album as ZIP ────────────────────────────
function DownloadAlbumButton({ photos, coupleName }) {
  const [status, setStatus] = useState('idle') // idle | downloading | done | error

  async function handleDownload() {
    if (status === 'downloading') return
    setStatus('downloading')
    try {
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()
      const folder = zip.folder('wedding-album')

      await Promise.all(photos.map(async (p, i) => {
        try {
          const res = await fetch(p.public_url)
          const blob = await res.blob()
          const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg'
          const name = p.uploader_name ? `${String(i+1).padStart(3,'0')}-${p.uploader_name.replace(/[^a-z0-9]/gi,'_')}.${ext}` : `${String(i+1).padStart(3,'0')}.${ext}`
          folder.file(name, blob)
        } catch { /* skip failed images */ }
      }))

      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${coupleName.replace(/[^a-z0-9&\s]/gi,'').trim()} - Wedding Album.zip`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
      setStatus('done')
      setTimeout(() => setStatus('idle'), 4000)
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }

  return (
    <div style={{borderTop:'1px solid #E5E0D8',paddingTop:'1.25rem',textAlign:'center'}}>
      <style>{`@keyframes albumSpin{to{transform:rotate(360deg)}}`}</style>
      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'rgba(3,1,0,0.45)',margin:'0 0 0.75rem',lineHeight:1.6}}>
        The wedding is over — but the memories live on. Download all {photos.length} guest photo{photos.length!==1?'s':''} in one album.
      </p>
      <button onClick={handleDownload} disabled={status==='downloading'}
        style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.85rem',color:status==='done'?'#3D8B5E':status==='error'?'#DC2626':'white',background:status==='done'?'rgba(61,139,94,0.1)':status==='error'?'rgba(220,38,38,0.08)':'#C4826A',border:`1.5px solid ${status==='done'?'rgba(61,139,94,0.3)':status==='error'?'rgba(220,38,38,0.2)':'#C4826A'}`,borderRadius:100,padding:'0.7rem 1.75rem',cursor:status==='downloading'?'default':'pointer',display:'inline-flex',alignItems:'center',gap:'0.5rem',transition:'all 0.2s',opacity:status==='downloading'?0.7:1}}>
        {status==='downloading' && <span style={{width:13,height:13,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'white',borderRadius:'50%',display:'inline-block',animation:'albumSpin 0.7s linear infinite'}}/>}
        {status==='idle'       && '⬇ Download Full Album (ZIP)'}
        {status==='downloading'&& 'Preparing your album…'}
        {status==='done'       && '✓ Download started!'}
        {status==='error'      && 'Something went wrong — try again'}
      </button>
    </div>
  )
}

// ── Guest Photo Upload ────────────────────────────────────
function GuestPhotoUpload({ weddingId, onUploaded }) {
  const [name, setName]       = useState('')
  const [uploading, setUploading] = useState(false)
  const [done, setDone]       = useState(false)

  async function handleFiles(e) {
    const files = Array.from(e.target.files); if (!files.length) return
    setUploading(true)
    for (const rawFile of files.slice(0,5)) {
      const file = await compressImage(rawFile)
      const ext = file.name.split('.').pop()
      const path = `guest-uploads/${weddingId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { error } = await supabase.storage.from('wedding-photos').upload(path, file)
      if (!error) {
        const { data } = supabase.storage.from('wedding-photos').getPublicUrl(path)
        await supabase.from('guest_photos').insert({ wedding_id:weddingId, uploader_name:name.trim()||null, public_url:data.publicUrl, storage_path:path })
        onUploaded(data.publicUrl)
      }
    }
    setUploading(false); setDone(true); setTimeout(()=>setDone(false),3000)
  }

  return (
    <div style={{background:'white',border:'1.5px dashed #E5E0D8',borderRadius:'1rem',padding:'1.25rem',display:'flex',flexDirection:'column',gap:'0.75rem'}}>
      <input className="input-field" placeholder="Your name (optional)" value={name} onChange={e=>setName(e.target.value)} style={{fontSize:'0.85rem'}}/>
      <label style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.5rem',background:done?'rgba(155,175,155,0.1)':'#C4826A',color:done?'#7A917A':'white',border:done?'1.5px solid #9BAF9B':'none',borderRadius:100,padding:'0.75rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',textAlign:'center'}}>
        {done?'✓ Photos shared! Thank you':'📸 Share your photos'}
        <input type="file" multiple accept="image/*" style={{display:'none'}} onChange={handleFiles} disabled={uploading}/>
      </label>
      {uploading&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'rgba(3,1,0,0.5)',textAlign:'center',margin:0}}>Uploading…</p>}
      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'rgba(3,1,0,0.28)',textAlign:'center',margin:0}}>Up to 5 photos at a time · visible to all guests</p>
    </div>
  )
}


// ── Story Photo Break ─────────────────────────────────────
function StoryPhoto({ photos, index }) {
  const photo = photos?.[index % (photos?.length||1)]
  if (!photo?.public_url) return null
  return (
    <div style={{ width:'100%', borderRadius:'1rem', overflow:'hidden', margin:'0.5rem 0' }}>
      <img src={photo.public_url} alt="" style={{ width:'100%', height:'auto', display:'block', borderRadius:'1rem' }}
        onError={e=>e.target.parentElement.style.display='none'}/>
    </div>
  )
}

// Reveal — fades + lifts its children in once they scroll into view. Calm,
// one-by-one as the guest scrolls. Uses IntersectionObserver, fires once.
function Reveal({ children, style }) {
  const ref = useRef(null)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setShown(true); return }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect() }
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return (
    <div ref={ref} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? 'none' : 'translateY(26px)',
      transition: 'opacity 0.9s cubic-bezier(0.22,1,0.36,1), transform 0.9s cubic-bezier(0.22,1,0.36,1)',
      willChange: 'opacity, transform',
      ...style,
    }}>{children}</div>
  )
}

// FlowCard — a slim "continue to another tab" card on the home page.
function FlowCard({ Icon, title, sub, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:'0.875rem', width:'100%', textAlign:'left',
        background:'white', border:'1px solid #E5E0D8', borderRadius:'1rem', padding:'0.9rem 1.1rem', cursor:'pointer', transition:'border-color 0.18s, transform 0.18s' }}
      onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(196,130,106,0.4)'; e.currentTarget.style.transform='translateY(-2px)' }}
      onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none' }}>
      <div style={{ width:38, height:38, borderRadius:'50%', background:'rgba(196,130,106,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon size={17} color="#C4826A"/></div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:0 }}>{title}</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.1rem 0 0' }}>{sub}</p>
      </div>
      <span style={{ color:'#C4826A', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, flexShrink:0 }}>→</span>
    </button>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function WeddingMiniSite() {
  const { weddingSlug } = useParams()
  const [searchParams]  = useSearchParams()
  const [wedding, setWedding] = useState(null)
  const [events, setEvents]   = useState([])
  const [programme, setProgramme] = useState([])
  const [photos, setPhotos]   = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState(searchParams.get('gift') ? 'registry' : (searchParams.get('tab') || 'home'))
  // Swipe between portal tabs on mobile (computed before any early return so the
  // hook order stays stable). Order must match the TABS strip below.
  const _portalVis = wedding?.portal_visibility || {}
  const swipeIds = ['home', ...['story','events','registry','asoebi','photos','programme','eyes'].filter(k => _portalVis[k] !== false)]
  const swipe = useSwipeTabs(swipeIds, tab, setTab)
  const storyRef = useRef(null)
  const [lightbox, setLightbox] = useState(null)
  const [guestPhotos, setGuestPhotos] = useState([])
  const [story, setStory]       = useState(null)
  const [asoEbi, setAsoEbi]           = useState(null)
  const [asoEbiPhotos, setAsoEbiPhotos] = useState([])
  const [portalUnlocked, setPortalUnlocked] = useState(false)
  const [passwordEntry, setPasswordEntry]   = useState('')
  const [passwordError, setPasswordError]   = useState(false)
  const [giftThanks, setGiftThanks]         = useState(null)
  const [giftRefresh, setGiftRefresh]       = useState(0)

  // After a guest returns from Paystack (?gift=<ref>), confirm + record the gift.
  useEffect(() => {
    const ref = searchParams.get('gift')
    if (!ref) return
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-gift`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ reference: ref }),
    }).then(r => r.json()).then(j => {
      if (!j.ok) return
      setGiftThanks(j.net || true)
      // `already` = an idempotent replay (StrictMode double-fire, refresh, or a
      // revisited ?gift= URL). Don't double-record the receipt on those.
      if (j.already) return
      // Keep a local receipt of this guest's gifts to this wedding (they aren't logged in).
      // Dedupe by reference too, so the same gift can never appear twice.
      try {
        const key = `okoiya_gifts_${weddingSlug}`
        const arr = JSON.parse(localStorage.getItem(key) || '[]')
        if (!arr.some(g => g.ref === ref)) {
          arr.push({ ref, amount: Number(j.net) || 0, at: Date.now() })
          localStorage.setItem(key, JSON.stringify(arr))
        }
      } catch (_) {}
      setGiftRefresh(k => k + 1)   // re-fetch the registry so the fund's bar updates
    }).catch(() => {})
    try { const u = new URL(window.location.href); u.searchParams.delete('gift'); window.history.replaceState({}, '', u.toString()) } catch (_) {}
  }, [])

  // Calm one-by-one fade-in for the story sections as the guest scrolls.
  // useLayoutEffect so the hidden state is set before paint (no flash).
  useLayoutEffect(() => {
    if (tab !== 'story') return
    const root = storyRef.current
    if (!root || typeof IntersectionObserver === 'undefined') return
    const kids = Array.from(root.children)
    kids.forEach(k => {
      k.style.opacity = '0'
      k.style.transform = 'translateY(26px)'
      k.style.transition = 'opacity 0.9s cubic-bezier(0.22,1,0.36,1), transform 0.9s cubic-bezier(0.22,1,0.36,1)'
      k.style.willChange = 'opacity, transform'
    })
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.style.opacity = '1'; e.target.style.transform = 'none'; io.unobserve(e.target) } })
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' })
    kids.forEach(k => io.observe(k))
    return () => io.disconnect()
  }, [tab, story])

  // Keep the active tab in view — e.g. tapping a home shortcut auto-scrolls the
  // tab strip so its name is visible without the guest scrolling manually.
  useEffect(() => {
    const el = document.querySelector(`#guest-tab-scroll [data-tab="${tab}"]`)
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [tab])

  useEffect(()=>{
    async function load() {
      try {
        // ── Step 1: Wedding (mandatory — nothing else matters without this) ──
        const { data:w, error:wErr } = await supabase
          .rpc('get_public_wedding', { p_slug: weddingSlug })
        if (wErr || !w) { setLoading(false); return }

        // Commit wedding + events immediately so the page is never stuck loading
        setWedding(w)
        setEvents(w.wedding_events||[])
        if (w.portal_password_protected) {
          const unlocked = sessionStorage.getItem(`portal_unlocked_${w.slug}`)
          if (unlocked === 'true') setPortalUnlocked(true)
        } else {
          setPortalUnlocked(true)
        }

        // ── Step 2: Supplementary data — each fetch is isolated ──
        await Promise.allSettled([
          // Programme
          supabase.from('programme_items').select('*').eq('wedding_id',w.id).order('sort_order')
            .then(({data})=>{ if(data) setProgramme(data) }),

          // Studio photos
          supabase.from('photos').select('*').eq('wedding_id',w.id).order('created_at',{ascending:false}).limit(30)
            .then(({data})=>{ if(data) setPhotos(data) }),

          // Guest photos
          supabase.from('guest_photos').select('*').eq('wedding_id',w.id).eq('hidden',false).order('created_at',{ascending:false})
            .then(({data})=>{ if(data) setGuestPhotos(data) }),

          // Aso Ebi
          supabase.from('asoebi_setup').select('*').eq('wedding_id',w.id).maybeSingle()
            .then(({data})=>{ if(data) setAsoEbi(data) }),
          supabase.from('asoebi_photos').select('*').eq('wedding_id',w.id).order('sort_order')
            .then(({data})=>{ if(data) setAsoEbiPhotos(data) }),

          // Love story
          supabase.from('wedding_story').select('*').eq('wedding_id',w.id).eq('published',true).maybeSingle()
            .then(({data})=>{ if(data) setStory(data) }),
        ])
      } catch(err) {
        console.error('[Portal] Load error:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  },[weddingSlug])

  if (loading) return (
    <div style={{minHeight:'100vh',background:'#FFFFFF',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC'}}>Just a second…</p>
    </div>
  )
  if (!wedding) return (
    <div style={{minHeight:'100vh',background:'#FFFFFF',display:'flex',alignItems:'center',justifyContent:'center',textAlign:'center',padding:'2rem'}}>
      <div><Heart size={32} color="#E5E0D8" style={{marginBottom:'1rem'}}/><h2 style={{fontFamily:'Gibs, serif',fontWeight:400}}>Wedding not found</h2></div>
    </div>
  )

  // Password gate
  if (wedding.portal_password_protected && !portalUnlocked) {
    async function handlePasswordSubmit() {
      const { data: ok } = await supabase.rpc('verify_portal_password', { p_slug: wedding.slug, p_attempt: passwordEntry })
      if (ok) {
        sessionStorage.setItem(`portal_unlocked_${wedding.slug}`, 'true')
        setPortalUnlocked(true)
        setPasswordError(false)
      } else {
        setPasswordError(true)
      }
    }
    return (
      <div style={{minHeight:'100vh',background:'#FAFAF8',display:'flex',alignItems:'center',justifyContent:'center',padding:'2rem 1rem'}}>
        <div style={{width:'100%',maxWidth:380,textAlign:'center'}}>
          <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.4rem',color:'#1A1A1A',margin:'0 0 0.5rem'}}>Private Wedding Portal</h2>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#9B9B9B',margin:'0 0 1.5rem',lineHeight:1.6}}>
            {wedding.partner1_name} & {wedding.partner2_name}'s wedding portal is password protected.
          </p>
          <input type="password" value={passwordEntry} onChange={e=>setPasswordEntry(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handlePasswordSubmit()}
            placeholder="Enter password" autoFocus
            style={{width:'100%',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.9rem',padding:'0.75rem 1rem',border:`1.5px solid ${passwordError?'#DC2626':'#E5E0D8'}`,borderRadius:'0.75rem',outline:'none',background:'white',boxSizing:'border-box',marginBottom:'0.5rem'}}/>
          {passwordError && <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#DC2626',margin:'0 0 0.75rem'}}>Incorrect password. Please try again.</p>}
          <button onClick={handlePasswordSubmit}
            style={{width:'100%',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',color:'white',background:'linear-gradient(135deg,#B36B55,#D4845A)',border:'none',borderRadius:100,padding:'0.875rem',cursor:'pointer'}}>
            Enter Portal →
          </button>
        </div>
      </div>
    )
  }

  const p1=wedding.partner1_name, p2=wedding.partner2_name
  const mainDate = events[0]?.date||wedding.wedding_date
  const daysLeft = mainDate ? Math.ceil((new Date(mainDate) - new Date()) / (1000*60*60*24)) : null
  const mainEvent = events[0]
  const vis = wedding?.portal_visibility || {}
  const tabVisible = (key) => vis[key] !== false
  const TABS = [
    { id:'home',       label:'Home' },
    ...(tabVisible('story')     ? [{ id:'story',     label:'Our Story' }]        : []),
    ...(tabVisible('events')    ? [{ id:'events',    label:'Events' }]           : []),
    ...(tabVisible('registry')  ? [{ id:'registry',  label:'Registry' }]         : []),
    ...(tabVisible('asoebi')    ? [{ id:'asoebi',    label:'Aso Ebi' }]          : []),
    ...(tabVisible('photos')    ? [{ id:'photos',    label:'Studio' }]           : []),
    ...(tabVisible('programme') ? [{ id:'programme', label:'Programme' }]        : []),
    ...(tabVisible('eyes')      ? [{ id:'eyes',      label:'Through Your Eyes' }]: []),
  ]

  const portalContent = (
    <div style={{minHeight:'100vh',background:'#FFFFFF',fontFamily:'Nunito Sans, sans-serif'}}>

      {giftThanks && (
        <div onClick={()=>setGiftThanks(false)} style={{ position:'fixed', top:16, left:'50%', transform:'translateX(-50%)', zIndex:9999, background:'white', border:'1px solid rgba(196,130,106,0.3)', borderRadius:100, padding:'0.65rem 1.25rem', boxShadow:'0 8px 30px rgba(0,0,0,0.15)', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:700, color:'#1A1A1A' }}>
          🎁 {typeof giftThanks === 'number' ? `Your ₦${Number(giftThanks).toLocaleString()} gift is on its way.` : 'Your gift is on its way.'} Thank you! 💛
        </div>
      )}

      {/* ── HERO ── */}
      {(()=>{
        // Carousel: use studio photos + defaults to always show 8
        const DEFAULT_PHOTOS = Array.from({length:8},(_,i)=>({ id:`def-${i}`, public_url:`/gallery-default-${i+1}.jpg` }))
        const studioSlice = photos.slice(0,8)
        const displayPhotos = [...studioSlice, ...DEFAULT_PHOTOS].slice(0,8)
        const carouselItems = [...displayPhotos, ...displayPhotos] // 16 for seamless loop

        const heroPhoto = (wedding?.hero_preset === 'photo' && wedding?.hero_image_url) ? wedding.hero_image_url : null
        const photoMode = !!heroPhoto
        const preset    = HERO_PRESETS[wedding?.hero_preset] || HERO_PRESETS.floral
        // The floral photo gets the same dark-scrim + white-text treatment as an
        // uploaded photo, so the names read cleanly instead of clashing with it.
        const darkHero  = photoMode || preset.type === 'image'
        const heroText  = darkHero ? '#FFFFFF' : '#1A1A1A'
        const heroSub   = darkHero ? 'rgba(255,255,255,0.82)' : 'rgba(28,20,16,0.45)'
        const heroAmp   = darkHero ? '#F2CBB9' : '#C4826A'

        return (
          <div style={{
            position:'relative',
            background:'#FAF6F0',
            backgroundImage: (!photoMode && preset.bg) ? preset.bg : undefined,
            backgroundSize:  (!photoMode && preset.type==='gradient') ? '400% 400%' : undefined,
            animation:       (!photoMode && preset.type==='gradient') ? 'heroGradient 12s ease infinite' : undefined,
            overflow:'hidden',
          }}>
            {/* Back image: uploaded photo (photo mode) or the floral preset */}
            {(photoMode || preset.type==='image') && (
              <img src={photoMode ? heroPhoto : preset.src} alt="" aria-hidden="true"
                onError={e=>{ e.currentTarget.style.display='none' }}
                style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'center', zIndex:0, pointerEvents:'none' }}/>
            )}
            {/* Warm dark scrim over any photo (uploaded or the floral preset) so
                the white hero text reads crisply and doesn't clash with the image */}
            {darkHero ? (
              <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, zIndex:0, pointerEvents:'none',
                background:'linear-gradient(180deg, rgba(26,16,9,0.58), rgba(26,16,9,0.40) 45%, rgba(18,11,6,0.72))' }}/>
            ) : null}
            {/* Falling petals behind all content */}
            <FallingPetals density={18}/>

            <style>{`
              @keyframes heroGradient {
                0%,100%{background-position:0% 50%}
                50%{background-position:100% 50%}
              }
              /* Mobile: 100×145px cards, 10px gap → offset = 8×(100+10) = 880px */
              @keyframes carouselScroll { to { transform:translateX(-880px); } }
              @media (min-width:769px) {
                /* Desktop: 150×210px cards, 12px gap → offset = 8×(150+12) = 1296px */
                @keyframes carouselScroll { to { transform:translateX(-1296px); } }
              }
              .hero-carousel-track {
                display:flex; gap:10px;
                width:1760px; /* 16×(100+10) */
                animation:carouselScroll 27.5s linear infinite;
              }
              .hero-carousel-track:hover { animation-play-state:paused; }
              @media (min-width:769px) {
                .hero-carousel-track { gap:12px; width:2592px; animation-duration:40s; }
              }
              .hero-carousel-card {
                width:100px; height:145px; border-radius:14px; overflow:hidden; flex-shrink:0;
              }
              @media (min-width:769px) {
                .hero-carousel-card { width:150px; height:210px; border-radius:18px; }
              }
            `}</style>

            {/* Top bar: Logo + Tagline */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'1.25rem 1.5rem 0',position:'relative',zIndex:1}}>
              <img src="/okoiya-logo.png" alt="Okoiya" style={{height:'clamp(20px,4.5vw,30px)',objectFit:'contain',display:'block',flexShrink:0,filter:darkHero?'brightness(0) invert(1)':'none'}}/>
              <p style={{fontFamily:'Great Vibes, cursive',fontSize:'clamp(0.9rem,2.2vw,1.25rem)',fontWeight:400,color:heroSub,margin:0,textAlign:'right',maxWidth:'60%',lineHeight:1.25,textShadow:darkHero?'0 1px 8px rgba(0,0,0,0.3)':'none'}}>
                {wedding?.hero_tagline || 'Where perfect weddings come together'}
              </p>
            </div>

            {/* Center: Names + Date + Countdown */}
            <div style={{textAlign:'center',padding:'1.75rem 1.5rem 1.75rem',position:'relative',zIndex:1}}>
              <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'clamp(0.6rem,1.5vw,0.72rem)',fontWeight:700,letterSpacing:'0.28em',textTransform:'uppercase',color:heroSub,margin:'0 0 0.85rem',textShadow:darkHero?'0 1px 8px rgba(0,0,0,0.35)':'none'}}>The Wedding of</p>
              <h1 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'clamp(1.8rem,5.5vw,3.4rem)',color:heroText,lineHeight:1.15,margin:'0 0 0.875rem',textShadow:darkHero?'0 2px 18px rgba(0,0,0,0.4)':'none'}}>
                {p1}
                <em style={{fontStyle:'italic',color:heroAmp,margin:'0 0.4rem',fontSize:'0.75em'}}>&amp;</em>
                {p2}
              </h1>
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.6rem',margin:'0 0 1.1rem'}}>
                <span style={{width:'clamp(28px,8vw,52px)',height:1,background:darkHero?'rgba(255,255,255,0.5)':'rgba(196,130,106,0.45)'}}/>
                <span style={{width:5,height:5,borderRadius:'1px',background:heroAmp,transform:'rotate(45deg)'}}/>
                <span style={{width:'clamp(28px,8vw,52px)',height:1,background:darkHero?'rgba(255,255,255,0.5)':'rgba(196,130,106,0.45)'}}/>
              </div>
              {mainDate&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'clamp(0.72rem,1.8vw,0.85rem)',color:heroSub,letterSpacing:'0.08em',textTransform:'uppercase',margin:'0 0 1.5rem',textShadow:darkHero?'0 1px 10px rgba(0,0,0,0.35)':'none'}}>
                {new Date(mainDate).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
              </p>}
              {mainDate&&<CountdownHeroLight date={mainDate} onDark={darkHero}/>}
            </div>

            {/* Carousel — hidden in photo mode unless the couple opted to keep it */}
            <div style={{overflow:'hidden',width:'100%',marginTop:'0.5rem',position:'relative',zIndex:1,display:(photoMode && !wedding?.hero_show_carousel)?'none':'block'}}>
              <div className="hero-carousel-track">
                {carouselItems.map((photo,i)=>(
                  <div key={i} className="hero-carousel-card">
                    <img src={photo.public_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:'center',display:'block'}}
                      onError={e=>{e.currentTarget.style.background='linear-gradient(135deg,#F0E6DA,#E8D5C0)';e.currentTarget.style.display='none';e.currentTarget.parentElement.style.background='linear-gradient(135deg,#F0E6DA,#E8D5C0)'}}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom spacer so carousel isn't flush against tabs */}
            <div style={{height:'1.5rem'}}/>
          </div>
        )
      })()}

      {/* Navigation — centred desktop, left-start swipeable mobile */}
      <div style={{position:'sticky',top:0,zIndex:20,background:'white',borderBottom:'1px solid #E5E0D8'}}>
        <div id="guest-tab-scroll" style={{display:'flex',overflowX:'auto',scrollbarWidth:'none',msOverflowStyle:'none',WebkitOverflowScrolling:'touch'}}>
          <div style={{display:'flex',margin:'0 auto'}}>
            {TABS.map(t=>(
              <button key={t.id} data-tab={t.id} onClick={()=>setTab(t.id)} style={{
                padding:'0 1rem',height:44,background:'none',border:'none',cursor:'pointer',
                fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',
                fontWeight:tab===t.id?700:500,
                color:tab===t.id?'#C4826A':'#6B6B6B',
                borderBottom:tab===t.id?'2px solid #C4826A':'2px solid transparent',
                whiteSpace:'nowrap',flexShrink:0,transition:'color 0.15s'
              }}>{t.label}</button>
            ))}
          </div>
        </div>
        {/* Swipe arrow — mobile only, scrolls tabs right when tapped */}
        <style>{`@keyframes guestTabNudge{0%,100%{transform:translateX(0)}50%{transform:translateX(4px)}}`}</style>
        <button onClick={()=>{ const el=document.getElementById('guest-tab-scroll'); if(el) el.scrollBy({left:140,behavior:'smooth'}) }}
          className="guest-swipe-arrow"
          style={{position:'absolute',right:0,top:0,height:48,width:44,border:'none',cursor:'pointer',
            background:'linear-gradient(to right, rgba(255,255,255,0), white 40%)',
            display:'flex',alignItems:'flex-end',justifyContent:'flex-end',paddingRight:'0.4rem',paddingBottom:'0.55rem'}}>
          <span style={{display:'flex',alignItems:'center',justifyContent:'center',width:22,height:22,borderRadius:'50%',
            background:'rgba(196,130,106,0.12)',color:'#C4826A',fontSize:'1.05rem',fontWeight:800,lineHeight:1,
            animation:'guestTabNudge 1.4s ease-in-out infinite'}}>›</span>
        </button>
      </div>

      <div {...swipe} style={{maxWidth:680,margin:'0 auto',padding:'2rem 1.25rem'}}>

        {/* HOME */}
        {tab==='home'&&(
          <div style={{display:'flex',flexDirection:'column',gap:'1.5rem'}}>

            {/* LIVE STREAM CARD — only on wedding day when stream link exists */}
            {wedding?.stream_link && (() => {
              const today = new Date().toDateString()
              const isWeddingDay = events.some(ev=>ev.date&&new Date(ev.date).toDateString()===today) ||
                (wedding.wedding_date&&new Date(wedding.wedding_date).toDateString()===today)
              if (!isWeddingDay) return null
              const url = wedding.stream_link
              const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|live\/|embed\/))([^&?#\s]+)/)
              const embedUrl = youtubeMatch ? `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=0&rel=0` : null
              return (
                <div style={{background:'linear-gradient(135deg,#1A1A1A,#2A1A10)',borderRadius:'1.25rem',overflow:'hidden',border:'1px solid rgba(196,130,106,0.3)'}}>
                  <div style={{padding:'1rem 1.25rem',display:'flex',alignItems:'center',gap:'0.625rem'}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:'#EF4444',animation:'livePulse 1.5s ease-in-out infinite',flexShrink:0}}/>
                    <style>{`@keyframes livePulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.6;transform:scale(1.3)}}`}</style>
                    <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.72rem',color:'white',letterSpacing:'0.12em',textTransform:'uppercase',margin:0}}>Live Now</p>
                    <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'rgba(255,255,255,0.6)',margin:'0 0 0 auto'}}>{wedding.partner1_name} & {wedding.partner2_name}</p>
                  </div>
                  {embedUrl ? (
                    <div style={{position:'relative',paddingBottom:'56.25%',height:0}}>
                      <iframe src={embedUrl} title="Live Stream" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                        style={{position:'absolute',top:0,left:0,width:'100%',height:'100%'}}/>
                    </div>
                  ) : (
                    <div style={{padding:'1.5rem 1.25rem',textAlign:'center'}}>
                      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'rgba(255,255,255,0.7)',margin:'0 0 1rem',lineHeight:1.6}}>The ceremony is happening live. Join us now.</p>
                      <a href={url} target="_blank" rel="noreferrer"
                        style={{display:'inline-flex',alignItems:'center',gap:'0.5rem',background:'#B36B55',color:'white',borderRadius:100,padding:'0.75rem 1.75rem',textDecoration:'none',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.85rem'}}>
                        ▶ Join Live Stream →
                      </a>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Events at a glance — a neat summary; full details live on the Events tab */}
            {events.length>0&&(()=>{
              const ev=events[0]
              return (
                <div onClick={()=>setTab('events')} className="card" style={{cursor:'pointer',transition:'border-color 0.18s'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(196,130,106,0.4)'}
                  onMouseLeave={e=>e.currentTarget.style.borderColor='#E5E0D8'}>
                  <div style={{display:'flex',gap:'0.875rem',alignItems:'flex-start'}}>
                    <div style={{width:40,height:40,background:'rgba(196,130,106,0.1)',borderRadius:'0.75rem',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <Calendar size={17} color="#C4826A"/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',gap:'0.5rem'}}>
                        <p style={{fontFamily:'Gibs, serif',fontSize:'1rem',color:'#1A1A1A',margin:0}}>{ev.type}</p>
                        {events.length>1&&<span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.62rem',fontWeight:700,color:'#C4826A',background:'rgba(196,130,106,0.08)',borderRadius:100,padding:'2px 9px',flexShrink:0,whiteSpace:'nowrap'}}>{events.length} events</span>}
                      </div>
                      {ev.date&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'rgba(3,1,0,0.5)',margin:'0.3rem 0 0'}}>{new Date(ev.date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>}
                      {ev.strictly_invited
                        ? <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'rgba(3,1,0,0.4)',margin:'0.15rem 0 0',fontStyle:'italic'}}>Venue shared on your invitation</p>
                        : (ev.venue&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'rgba(3,1,0,0.5)',margin:'0.15rem 0 0',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.venue}</p>)}
                    </div>
                  </div>
                  <div style={{borderTop:'1px solid #F0EDE7',marginTop:'0.875rem',paddingTop:'0.75rem'}}>
                    <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#C4826A'}}>View all events &amp; directions →</span>
                  </div>
                </div>
              )
            })()}

            {/* Anticipation CTA — points to the Order of the Day so a guest who's
                joining knows exactly how the celebration will unfold (falls back
                to Events if the couple hasn't published a programme). */}
            {(()=>{
              const hasProg = tabVisible('programme')
              return (
                <div style={{background:'linear-gradient(135deg,rgba(196,130,106,0.08),rgba(155,175,155,0.06))',border:'1px solid rgba(196,130,106,0.18)',borderRadius:'1.25rem',padding:'1.25rem 1.5rem',textAlign:'center'}}>
                  <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.2rem',color:'#1A1A1A',margin:'0 0 0.3rem'}}>Will you be joining us?</p>
                  <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#6B6B6B',margin:'0 0 1rem',lineHeight:1.6}}>
                    {hasProg
                      ? "We've planned every moment with you in mind — take a peek at how the day will unfold."
                      : "It will mean the world to us — here's everything you'll need to be there."}
                  </p>
                  <button onClick={()=>setTab(hasProg?'programme':'events')} style={{background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.65rem 1.75rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.85rem'}}>
                    {hasProg ? 'See the Order of the Day →' : "See the day's details →"}
                  </button>
                </div>
              )
            })()}

{/* Story teaser */}
            {story?.our_story&&(
              <div onClick={()=>setTab('story')} style={{background:'white',border:'1px solid #E5E0D8',borderRadius:'1.25rem',padding:'1.1rem 1.25rem',cursor:'pointer'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(196,130,106,0.4)'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#E5E0D8'}>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.82rem',color:'#1A1A1A',margin:'0 0 0.5rem'}}>Our Story</p>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#6B6B6B',margin:'0 0 0.75rem',lineHeight:1.7,display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
                  {story.our_story}
                </p>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#C4826A',fontWeight:600}}>Read more →</span>
              </div>
            )}

            {/* Flow to the other tabs — a gentle invitation to explore */}
            {(tabVisible('registry')||tabVisible('asoebi')||tabVisible('photos'))&&(
              <div>
                <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.15rem',color:'#1A1A1A',margin:'0.5rem 0 0.875rem',textAlign:'center'}}>Be part of our day</p>
                <div style={{display:'flex',flexDirection:'column',gap:'0.625rem'}}>
                  {tabVisible('registry')&&<FlowCard Icon={Gift} title="Gift Registry" sub="Bless us with something special" onClick={()=>setTab('registry')}/>}
                  {tabVisible('asoebi')&&<FlowCard Icon={Shirt} title="Aso Ebi" sub="Get our colours & fabric" onClick={()=>setTab('asoebi')}/>}
                  {tabVisible('photos')&&<FlowCard Icon={Camera} title="Studio" sub="Browse & share our gallery" onClick={()=>setTab('photos')}/>}
                </div>
              </div>
            )}

          </div>
        )}

        {/* OUR STORY */}
        {tab==='story'&&(
          <div style={{display:'flex',flexDirection:'column',gap:'1.5rem'}}>
            {!story?(
              <div style={{textAlign:'center',padding:'3rem 1rem'}}>
                <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',color:'#BCBCBC',margin:'0 0 0.5rem'}}>Something beautiful is being written — come back soon.</p>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#BCBCBC'}}>The couple's love story will appear here before the big day.</p>
              </div>
            ):(
              <div style={{display:'flex',flexDirection:'column',gap:'1.5rem'}}>
                {story.song_url&&(
                  <Reveal>
                    <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.6rem'}}>
                      <style>{`@keyframes notePulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.18);opacity:0.55}}`}</style>
                      <span style={{fontSize:'0.95rem',animation:'notePulse 1.8s ease-in-out infinite'}}>🎵</span>
                      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:0,fontStyle:'italic'}}>Press play — our story feels better with our song on.</p>
                    </div>
                    <div style={{overflow:'hidden',borderRadius:'0.875rem'}}>
                    {story.song_url.includes('spotify.com')&&(()=>{
                      const m=story.song_url.match(/\/(track|album|playlist)\/([a-zA-Z0-9]+)/)
                      if(!m)return null
                      return <iframe style={{borderRadius:'0.875rem',width:'100%',border:'none'}} src={`https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator&theme=0`} height={m[1]==='track'?80:152} allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"/>
                    })()}
                    {story.song_url.includes('youtu')&&!story.song_url.includes('spotify')&&(()=>{
                      const m=story.song_url.match(/(?:youtu\.be\/|v=)([a-zA-Z0-9_-]+)/)
                      if(!m)return null
                      return <iframe style={{borderRadius:'0.875rem',width:'100%',border:'none'}} src={`https://www.youtube.com/embed/${m[1]}`} height={200} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" loading="lazy"/>
                    })()}
                    {!story.song_url.includes('spotify')&&!story.song_url.includes('youtu')&&(
                      <div style={{background:'white',border:'1px solid #E5E0D8',borderRadius:'1rem',padding:'0.875rem 1.125rem',display:'flex',alignItems:'center',gap:'0.75rem'}}>
                        <span style={{fontSize:'1.1rem'}}>🎵</span>
                        <a href={story.song_url} target="_blank" rel="noopener noreferrer" style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#C4826A',textDecoration:'none',fontWeight:700}}>Listen to our song ↗</a>
                      </div>
                    )}
                    </div>
                  </Reveal>
                )}
                {story.write_mode==='free'&&story.free_write_content&&(
                  <Reveal>
                    <div className="card" style={{padding:'1.5rem'}}>
                      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.9rem',color:'#1A1A1A',lineHeight:1.8,whiteSpace:'pre-wrap'}}>{story.free_write_content}</p>
                    </div>
                  </Reveal>
                )}
                {story.write_mode!=='free'&&(
                  <div ref={storyRef} style={{display:'flex',flexDirection:'column',gap:'1.5rem'}}>
                    {(story.his_how_met||story.her_how_met)&&(
                      <div>
                        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.68rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.1em',margin:'0 0 0.875rem'}}>01 — How It Started</p>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'1rem'}}>
                          {story.his_how_met&&<div className="card" style={{padding:'1.125rem'}}>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:700,color:'#C4826A',margin:'0 0 0.5rem'}}>His version 💍</p>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#1A1A1A',lineHeight:1.75,margin:0}}>{story.his_how_met}</p>
                          </div>}
                          {story.her_how_met&&<div className="card" style={{padding:'1.125rem'}}>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:700,color:'#9BAF9B',margin:'0 0 0.5rem'}}>Her version 💐</p>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#1A1A1A',lineHeight:1.75,margin:0}}>{story.her_how_met}</p>
                          </div>}
                        </div>
                      </div>
                    )}
                    {story.relationship_story&&(
                      <div>
                        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.68rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.1em',margin:'0 0 0.875rem'}}>02 — Getting to Know Each Other</p>
                        <div className="card" style={{padding:'1.125rem'}}>
                          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#1A1A1A',lineHeight:1.8,margin:0}}>{story.relationship_story}</p>
                        </div>
                      </div>
                    )}
                    <StoryPhoto photos={photos} index={0}/>
                    {story.milestones?.length>0&&(
                      <div>
                        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.68rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.1em',margin:'0 0 0.875rem'}}>03 — Moments That Shaped Us</p>
                        <div style={{display:'flex',flexDirection:'column',gap:0,position:'relative',paddingLeft:'1.25rem'}}>
                          <div style={{position:'absolute',left:6,top:8,bottom:8,width:1.5,background:'#E5E0D8'}}/>
                          {story.milestones.map((m,i)=>(
                            <div key={i} style={{paddingBottom:'1rem',position:'relative'}}>
                              <div style={{position:'absolute',left:-20,top:4,width:10,height:10,borderRadius:'50%',background:'#C4826A',border:'2px solid white'}}/>
                              {m.date&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#C4826A',fontWeight:700,margin:'0 0 0.15rem'}}>{new Date(m.date+'T00:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</p>}
                              <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',color:'#1A1A1A',margin:'0 0 0.2rem'}}>{m.title}</p>
                              {m.desc&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'rgba(3,1,0,0.5)',lineHeight:1.65,margin:0}}>{m.desc}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(story.his_proposal||story.her_proposal)&&(
                      <div>
                        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.68rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.1em',margin:'0 0 0.875rem'}}>04 — The Question 💍</p>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'1rem'}}>
                          {story.his_proposal&&<div className="card" style={{padding:'1.125rem'}}>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:700,color:'#C4826A',margin:'0 0 0.5rem'}}>His side 💍</p>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#1A1A1A',lineHeight:1.75,margin:0}}>{story.his_proposal}</p>
                          </div>}
                          {story.her_proposal&&<div className="card" style={{padding:'1.125rem'}}>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:700,color:'#9BAF9B',margin:'0 0 0.5rem'}}>Her side 💐</p>
                            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#1A1A1A',lineHeight:1.75,margin:0}}>{story.her_proposal}</p>
                          </div>}
                        </div>
                      </div>
                    )}
                    <StoryPhoto photos={photos} index={1}/>
                    {(story.his_why||story.her_why)&&(
                      <div>
                        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.68rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.1em',margin:'0 0 0.875rem'}}>05 — Why I Chose You</p>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'1rem'}}>
                          {story.his_why&&<div style={{background:'linear-gradient(135deg,rgba(196,130,106,0.05),rgba(196,130,106,0.02))',border:'1px solid rgba(196,130,106,0.2)',borderRadius:'1rem',padding:'1.125rem'}}>
                            <p style={{fontFamily:'Gibs, serif',fontStyle:'italic',fontSize:'0.9rem',color:'#1A1A1A',lineHeight:1.8,margin:0}}>"{story.his_why}"</p>
                          </div>}
                          {story.her_why&&<div style={{background:'linear-gradient(135deg,rgba(155,175,155,0.05),rgba(155,175,155,0.02))',border:'1px solid rgba(155,175,155,0.2)',borderRadius:'1rem',padding:'1.125rem'}}>
                            <p style={{fontFamily:'Gibs, serif',fontStyle:'italic',fontSize:'0.9rem',color:'#1A1A1A',lineHeight:1.8,margin:0}}>"{story.her_why}"</p>
                          </div>}
                        </div>
                      </div>
                    )}
                    <StoryPhoto photos={photos} index={2}/>
                    {story.looking_forward&&(
                      <div style={{textAlign:'center',padding:'1.5rem',background:'#FAFAF8',borderRadius:'1rem',border:'1px solid #E5E0D8'}}>
                        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.68rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.1em',margin:'0 0 0.75rem'}}>06 — And Now…</p>
                        <p style={{fontFamily:'Gibs, serif',fontStyle:'italic',fontSize:'1rem',color:'#1A1A1A',lineHeight:1.8,margin:'0 0 1.25rem'}}>{story.looking_forward}</p>
                        {mainDate&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#C4826A',fontWeight:700}}>{new Date(mainDate).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* EVENTS */}
        {tab==='events'&&(
          <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
            <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.4rem',margin:'0 0 0.5rem'}}>Our Events</h2>
            {events.map(ev=>(
              <div key={ev.id} className="card">
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.72rem',color:'#C4826A',textTransform:'uppercase',letterSpacing:'0.06em',margin:'0 0 0.5rem'}}>{ev.type}</p>
                {ev.date&&<div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.35rem'}}><Calendar size={14} color="#6B6B6B"/><span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#1A1A1A'}}>{new Date(ev.date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</span></div>}
                {ev.strictly_invited&&<span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:700,color:'#C4826A',background:'rgba(196,130,106,0.08)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:100,padding:'3px 12px',display:'inline-block',marginBottom:'0.35rem'}}>Strictly by Invitation</span>}
                {ev.strictly_invited ? (
                  <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <MapPin size={14} color="#6B6B6B"/>
                    <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'rgba(3,1,0,0.5)',fontStyle:'italic'}}>Venue is shared on your personal invitation</span>
                  </div>
                ) : ev.venue&&(
                  <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <MapPin size={14} color="#6B6B6B"/>
                    <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'rgba(3,1,0,0.5)'}}>{ev.venue}</span>
                  </div>
                )}
                {ev.dress_code&&<div style={{marginTop:'0.75rem',paddingTop:'0.75rem',borderTop:'1px solid #E5E0D8'}}><p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#9BAF9B',margin:0}}>Dress Code: {ev.dress_code}</p></div>}
                {(ev.date || (ev.venue && !ev.strictly_invited)) && (
                  <div style={{display:'flex',justifyContent:'flex-start',alignItems:'center',gap:'0.5rem',flexWrap:'wrap',marginTop:'0.875rem',paddingTop:'0.875rem',borderTop:'1px solid #F3F0EA'}}>
                    {ev.venue && !ev.strictly_invited && (
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ev.venue)}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{display:'inline-flex',alignItems:'center',gap:'0.35rem',background:'rgba(196,130,106,0.08)',border:'1px solid rgba(196,130,106,0.25)',borderRadius:100,padding:'0.45rem 1rem',textDecoration:'none'}}>
                        <MapPin size={12} color="#C4826A"/>
                        <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:'#C4826A'}}>Get Directions</span>
                      </a>
                    )}
                    <AddToCalendar ev={ev} couple={`${wedding?.partner1_name||''} & ${wedding?.partner2_name||''}`.trim()}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* PROGRAMME */}
        {tab==='programme'&&(()=>{
          const today = new Date()
          const firstEvent = events[0]
          const eventDate = firstEvent?.date ? new Date(firstEvent.date) : null
          const isUnlocked = !eventDate || today >= eventDate
          const publishedItems = programme.filter(p=>p.published)
          function downloadPDF() {
            const html = `<!DOCTYPE html><html><head><title>Programme</title><style>body{font-family:Georgia,serif;max-width:600px;margin:40px auto;color:#1A1A1A}h1{font-size:1.6rem;font-weight:400;margin-bottom:0.25rem}.sub{color:#6B6B6B;font-size:0.85rem;margin-bottom:2rem}.item{display:flex;gap:1rem;padding:0.5rem 0;border-bottom:1px solid #F3F0EA}.time{color:#C4826A;font-size:0.75rem;min-width:45px}.title{font-size:0.9rem;font-weight:600}.dur{color:#BCBCBC;font-size:0.7rem}</style></head><body><h1>Order of Service</h1><p class="sub">${events.map(e=>e.type).join(' · ')}</p>${publishedItems.map(i=>`<div class="item"><div class="time">${i.time_label||''}</div><div><div class="title">${i.title}</div><div class="dur">${i.duration_mins?i.duration_mins+'min':''} ${i.description||''}</div></div></div>`).join('')}<p style="color:#BCBCBC;font-size:0.7rem;margin-top:2rem;text-align:center">Made with <a href="https://okoiya.com" style="color:#C4826A;text-decoration:none;">Okoiya</a></p></body></html>`
            const blob = new Blob([html],{type:'text/html;charset=utf-8'})
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a'); a.href=url; a.download='programme.html'; document.body.appendChild(a); a.click(); document.body.removeChild(a)
          }
          return (
            <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'0.5rem'}}>
                <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.4rem',margin:0}}>Order of Service</h2>
                {isUnlocked && publishedItems.length>0 && (
                  <button onClick={downloadPDF} style={{display:'flex',alignItems:'center',gap:'0.35rem',background:'white',border:'1px solid #E5E0D8',borderRadius:100,padding:'0.4rem 0.875rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#1A1A1A'}}>
                    ⬇ Download PDF
                  </button>
                )}
              </div>
              {!isUnlocked ? (
                <div style={{textAlign:'center',padding:'3rem 1rem',background:'#FAFAF8',borderRadius:'1rem',border:'1px solid #E5E0D8'}}>
                  <p style={{fontSize:'2rem',margin:'0 0 0.75rem'}}>🔒</p>
                  <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',color:'#1A1A1A',margin:'0 0 0.4rem'}}>Programme Not Yet Available</p>
                  <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.85rem',color:'rgba(3,1,0,0.5)',margin:0,lineHeight:1.65}}>The Order of Service will be revealed on the wedding day.</p>
                </div>
              ) : publishedItems.length===0 ? (
                <p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem',fontSize:'0.875rem'}}>The order of service will be up here on the day — check back.</p>
              ) : publishedItems.map((item,i)=>(
                <div key={item.id} style={{display:'flex',gap:'1rem',alignItems:'flex-start',padding:'0.875rem 0',borderBottom:'1px solid #E5E0D8'}}>
                  <div style={{textAlign:'right',flexShrink:0,minWidth:50}}>
                    {item.time_label&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#C4826A',fontWeight:700,margin:0}}>{item.time_label}</p>}
                    {item.duration_mins&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',color:'#BCBCBC',margin:0}}>{item.duration_mins}min</p>}
                  </div>
                  <div style={{flex:1}}>
                    <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',color:'#1A1A1A',margin:0}}>{item.title}</p>
                    {item.description&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'rgba(3,1,0,0.5)',margin:'0.2rem 0 0'}}>{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )
        })()}

        {/* REGISTRY */}
        {tab==='registry'&&(
          <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
            <div style={{textAlign:'center',marginBottom:'0.5rem'}}>
              <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.4rem',margin:'0 0 0.5rem'}}>Gift Registry</h2>
              <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'rgba(3,1,0,0.5)'}}>Your presence is our greatest gift. But if you'd like to give more…</p>
            </div>
            <RegistrySection weddingId={wedding.id} wedding={wedding} slug={weddingSlug} refreshKey={giftRefresh}/>
          </div>
        )}

        {/* STUDIO */}
        {tab==='photos'&&(
          <div>
            <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.4rem',margin:'0 0 0.25rem'}}>Studio</h2>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#9B9B9B',margin:'0 0 1.25rem',fontStyle:'italic'}}>Before the forever began</p>
            {photos.length===0?(
              <div style={{textAlign:'center',padding:'3rem',color:'#BCBCBC'}}>
                <Camera size={32} style={{marginBottom:'0.75rem'}}/>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem'}}>The couple's photos will appear here — check back soon.</p>
              </div>
            ):(
              <div style={{columnCount:'auto',columnWidth:'160px',columnGap:'0.75rem'}}>
                {photos.map((photo,i)=>(
                  <div key={photo.id} onClick={()=>setLightbox({images:photos.map(p=>p.public_url),index:i})}
                    style={{breakInside:'avoid',marginBottom:'0.75rem',borderRadius:'0.75rem',overflow:'hidden',cursor:'pointer'}}>
                    <img src={photo.public_url} alt="" style={{width:'100%',display:'block',borderRadius:'0.75rem'}}
                      onError={e=>e.target.style.display='none'}/>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* THROUGH YOUR EYES */}
        {tab==='asoebi'&&(
          <AsoEbiGuestView
            asoEbi={asoEbi}
            asoEbiPhotos={asoEbiPhotos}
            weddingId={wedding.id}
            wedding={wedding}
            daysLeft={daysLeft}
            isClosed={false}
          />
        )}

        {tab==='eyes'&&(()=>{
          const eyesEventDate = events[0]?.date ? new Date(events[0].date) : null
          const eyesUnlocked  = !eyesEventDate || new Date() >= eyesEventDate
          const weddingPassed = eyesEventDate && new Date() > eyesEventDate
          return (
          <div style={{display:'flex',flexDirection:'column',gap:'1.5rem'}}>
            <div style={{textAlign:'center'}}>
              <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.4rem',margin:'0 0 0.4rem'}}>Through Your Eyes</h2>
              <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'rgba(3,1,0,0.5)'}}>Share the moments you captured on the day. Every photo is a gift.</p>
            </div>
            {!eyesUnlocked ? (
              <div style={{textAlign:'center',padding:'3rem 1rem',background:'#FAFAF8',borderRadius:'1rem',border:'1px solid #E5E0D8'}}>
                <p style={{fontSize:'2rem',margin:'0 0 0.75rem'}}>📸</p>
                <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',color:'#1A1A1A',margin:'0 0 0.4rem'}}>Opens on the Wedding Day</p>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'rgba(3,1,0,0.5)',margin:0}}>Come back on the day to share your photos with the couple.</p>
              </div>
            ) : (
              <>
            <GuestPhotoUpload weddingId={wedding.id} onUploaded={url=>{
              setGuestPhotos(prev=>[{id:Date.now(),public_url:url,created_at:new Date().toISOString()},...prev])
            }}/>
            {guestPhotos.length===0?(
              <div style={{textAlign:'center',padding:'3rem 1rem',color:'rgba(3,1,0,0.28)'}}>
                <span style={{fontSize:'2.5rem',display:'block',marginBottom:'0.75rem'}}>📸</span>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem'}}>No photos yet. Be the first to capture a moment — tap below to upload. 📸</p>
              </div>
            ):(
              <div style={{columnCount:'auto',columnWidth:'160px',columnGap:'0.625rem'}}>
                {guestPhotos.map((p,i)=>(
                  <div key={p.id} style={{breakInside:'avoid',marginBottom:'0.625rem',borderRadius:'0.75rem',overflow:'hidden',cursor:'pointer'}}>
                    <img src={p.public_url} alt={p.uploader_name||''} onClick={()=>setLightbox({images:guestPhotos.map(g=>g.public_url),index:i})}
                      style={{width:'100%',display:'block',borderRadius:'0.75rem'}}
                      onError={e=>e.target.style.display='none'}/>
                    {p.uploader_name&&<div style={{padding:'4px 8px 6px',background:'white'}}>
                      <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',color:'rgba(3,1,0,0.5)'}}>📷 {p.uploader_name}</span>
                    </div>}
                  </div>
                ))}
              </div>
            )}
            {weddingPassed && guestPhotos.length > 0 && (
              <DownloadAlbumButton photos={guestPhotos} coupleName={`${wedding.partner1_name} & ${wedding.partner2_name}`}/>
            )}
              </>
            )}
          </div>
          )
        })()}


      </div>

      {/* ── Shared referral footer — visible on every tab ── */}
      <div style={{textAlign:'center',padding:'1.5rem 1.5rem 2rem',borderTop:'1px solid #E5E0D8',marginTop:'0.5rem'}}>
        {wedding?.founding && (
          <div style={{marginBottom:'0.875rem'}}>
            <span style={{display:'inline-flex',alignItems:'center',gap:'0.4rem',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.64rem',fontWeight:800,letterSpacing:'0.1em',textTransform:'uppercase',color:'#8A6D3B',background:'rgba(212,175,55,0.14)',border:'1px solid rgba(212,175,55,0.4)',borderRadius:100,padding:'0.35rem 0.9rem'}}>
              <Star size={11} fill="#C9A84C" color="#C9A84C"/> Founding Couple of Okoiya
            </span>
          </div>
        )}
        <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#BCBCBC',margin:'0.4rem 0 0'}}>
          Planning your own wedding?{' '}
          <a href="/signup" style={{color:'#C4826A',fontWeight:700,textDecoration:'none'}}>
            Become an Okoiya Couple today →
          </a>
        </p>
      </div>

      {/* Lightbox */}
      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={()=>setLightbox(null)}/>}
    </div>
  )
  return portalContent
}
